/**
 * Custom SVG illustrations for empty states
 * These provide delightful visuals instead of plain emojis
 */

interface IllustrationProps {
  className?: string
}

export function NoStoresIllustration({ className = 'w-32 h-32' }: IllustrationProps) {
  return (
    <svg className={className} viewBox="0 0 200 200" fill="none">
      {/* Building */}
      <rect x="50" y="80" width="100" height="80" rx="4" fill="var(--color-surface-tertiary)" stroke="var(--color-border-default)" strokeWidth="2"/>
      
      {/* Roof */}
      <path d="M40 85L100 45L160 85" stroke="var(--color-brand-primary)" strokeWidth="3" strokeLinecap="round"/>
      
      {/* Door */}
      <rect x="85" y="120" width="30" height="40" rx="2" fill="var(--color-surface-elevated)" stroke="var(--color-border-default)" strokeWidth="2"/>
      <circle cx="108" cy="140" r="3" fill="var(--color-brand-primary)"/>
      
      {/* Windows */}
      <rect x="58" y="95" width="20" height="15" rx="2" fill="var(--color-surface-elevated)" stroke="var(--color-border-light)" strokeWidth="1.5"/>
      <rect x="122" y="95" width="20" height="15" rx="2" fill="var(--color-surface-elevated)" stroke="var(--color-border-light)" strokeWidth="1.5"/>
      
      {/* Closed sign */}
      <rect x="70" y="55" width="60" height="20" rx="4" fill="var(--color-status-error)" fillOpacity="0.1" stroke="var(--color-status-error)" strokeWidth="1.5"/>
      <text x="100" y="69" textAnchor="middle" fill="var(--color-status-error)" fontSize="10" fontWeight="600">CLOSED</text>
      
      {/* Decorative elements */}
      <circle cx="35" cy="120" r="4" fill="var(--color-brand-primary)" fillOpacity="0.3"/>
      <circle cx="165" cy="100" r="6" fill="var(--color-brand-primary)" fillOpacity="0.2"/>
      <circle cx="170" cy="150" r="3" fill="var(--color-brand-primary)" fillOpacity="0.4"/>
    </svg>
  )
}

export function NoDeliveryIllustration({ className = 'w-32 h-32' }: IllustrationProps) {
  return (
    <svg className={className} viewBox="0 0 200 200" fill="none">
      {/* Road */}
      <path d="M20 160H180" stroke="var(--color-border-default)" strokeWidth="4" strokeLinecap="round"/>
      <path d="M60 160V140" stroke="var(--color-border-light)" strokeWidth="2" strokeDasharray="4 4"/>
      <path d="M100 160V140" stroke="var(--color-border-light)" strokeWidth="2" strokeDasharray="4 4"/>
      <path d="M140 160V140" stroke="var(--color-border-light)" strokeWidth="2" strokeDasharray="4 4"/>
      
      {/* Scooter body */}
      <ellipse cx="100" cy="130" rx="35" ry="20" fill="var(--color-surface-tertiary)" stroke="var(--color-border-default)" strokeWidth="2"/>
      
      {/* Wheels */}
      <circle cx="70" cy="145" r="12" fill="var(--color-surface-elevated)" stroke="var(--color-text-tertiary)" strokeWidth="3"/>
      <circle cx="130" cy="145" r="12" fill="var(--color-surface-elevated)" stroke="var(--color-text-tertiary)" strokeWidth="3"/>
      
      {/* Handle */}
      <path d="M75 110C75 100 85 90 100 90" stroke="var(--color-text-tertiary)" strokeWidth="4" strokeLinecap="round"/>
      
      {/* Delivery box */}
      <rect x="95" y="95" width="30" height="25" rx="3" fill="var(--color-brand-primary)" stroke="var(--color-brand-primary)" strokeWidth="2"/>
      <path d="M100 107.5H120" stroke="white" strokeWidth="2" strokeLinecap="round"/>
      
      {/* X mark */}
      <circle cx="145" cy="70" r="20" fill="var(--color-status-error)" fillOpacity="0.1" stroke="var(--color-status-error)" strokeWidth="2"/>
      <path d="M137 62L153 78M153 62L137 78" stroke="var(--color-status-error)" strokeWidth="3" strokeLinecap="round"/>
      
      {/* Location pin */}
      <path d="M50 60C50 45 35 45 35 60C35 70 50 80 50 80C50 80 65 70 65 60C65 45 50 45 50 60Z" fill="var(--color-brand-primary)" fillOpacity="0.2" stroke="var(--color-brand-primary)" strokeWidth="2"/>
      <circle cx="50" cy="58" r="4" fill="var(--color-brand-primary)"/>
    </svg>
  )
}

export function LocationIllustration({ className = 'w-32 h-32' }: IllustrationProps) {
  return (
    <svg className={className} viewBox="0 0 200 200" fill="none">
      {/* Map background */}
      <rect x="30" y="50" width="140" height="100" rx="8" fill="var(--color-surface-tertiary)" stroke="var(--color-border-default)" strokeWidth="2"/>
      
      {/* Grid lines */}
      <path d="M50 50V150" stroke="var(--color-border-light)" strokeWidth="1"/>
      <path d="M80 50V150" stroke="var(--color-border-light)" strokeWidth="1"/>
      <path d="M120 50V150" stroke="var(--color-border-light)" strokeWidth="1"/>
      <path d="M150 50V150" stroke="var(--color-border-light)" strokeWidth="1"/>
      <path d="M30 80H170" stroke="var(--color-border-light)" strokeWidth="1"/>
      <path d="M30 110H170" stroke="var(--color-border-light)" strokeWidth="1"/>
      
      {/* Location pin */}
      <path d="M100 50C85 50 70 65 70 80C70 100 100 130 100 130C100 130 130 100 130 80C130 65 115 50 100 50Z" fill="var(--color-brand-primary)" fillOpacity="0.2" stroke="var(--color-brand-primary)" strokeWidth="3"/>
      <circle cx="100" cy="78" r="10" fill="var(--color-brand-primary)"/>
      <circle cx="100" cy="78" r="4" fill="white"/>
      
      {/* Pulse rings */}
      <circle cx="100" cy="78" r="20" stroke="var(--color-brand-primary)" strokeWidth="2" strokeOpacity="0.3" fill="none"/>
      <circle cx="100" cy="78" r="30" stroke="var(--color-brand-primary)" strokeWidth="1.5" strokeOpacity="0.2" fill="none"/>
      
      {/* Compass */}
      <circle cx="155" cy="65" r="12" fill="var(--color-surface-elevated)" stroke="var(--color-border-default)" strokeWidth="1.5"/>
      <path d="M155 57L158 70H152L155 57Z" fill="var(--color-brand-primary)"/>
      <path d="M155 73L152 60H158L155 73Z" fill="var(--color-text-tertiary)"/>
    </svg>
  )
}

export function NetworkErrorIllustration({ className = 'w-32 h-32' }: IllustrationProps) {
  return (
    <svg className={className} viewBox="0 0 200 200" fill="none">
      {/* Cloud */}
      <path d="M45 110C35 110 30 100 35 92C30 85 35 75 50 75C55 60 75 55 90 65C100 55 120 55 130 70C145 70 155 82 150 95C160 100 160 115 145 120H45Z" fill="var(--color-surface-tertiary)" stroke="var(--color-border-default)" strokeWidth="2"/>
      
      {/* Signal waves - crossed out */}
      <path d="M80 130C75 125 75 115 80 110" stroke="var(--color-text-tertiary)" strokeWidth="3" strokeLinecap="round"/>
      <path d="M90 135C82 127 82 113 90 105" stroke="var(--color-text-tertiary)" strokeWidth="3" strokeLinecap="round"/>
      <path d="M100 140C89 130 89 110 100 100" stroke="var(--color-text-tertiary)" strokeWidth="3" strokeLinecap="round"/>
      
      {/* X mark */}
      <circle cx="120" cy="130" r="22" fill="var(--color-status-error)" fillOpacity="0.1" stroke="var(--color-status-error)" strokeWidth="2"/>
      <path d="M110 120L130 140M130 120L110 140" stroke="var(--color-status-error)" strokeWidth="3" strokeLinecap="round"/>
      
      {/* Decorative dots */}
      <circle cx="50" cy="145" r="4" fill="var(--color-brand-primary)" fillOpacity="0.3"/>
      <circle cx="150" cy="155" r="3" fill="var(--color-brand-primary)" fillOpacity="0.4"/>
      <circle cx="70" cy="160" r="5" fill="var(--color-brand-primary)" fillOpacity="0.2"/>
    </svg>
  )
}

export function EmptySearchIllustration({ className = 'w-32 h-32' }: IllustrationProps) {
  return (
    <svg className={className} viewBox="0 0 200 200" fill="none">
      {/* Magnifying glass */}
      <circle cx="85" cy="85" r="40" fill="var(--color-surface-tertiary)" stroke="var(--color-border-default)" strokeWidth="3"/>
      <circle cx="85" cy="85" r="30" fill="var(--color-surface-elevated)" stroke="var(--color-border-light)" strokeWidth="2"/>
      
      {/* Handle */}
      <path d="M115 115L145 145" stroke="var(--color-text-tertiary)" strokeWidth="8" strokeLinecap="round"/>
      <path d="M115 115L145 145" stroke="var(--color-brand-primary)" strokeWidth="4" strokeLinecap="round"/>
      
      {/* Question mark */}
      <path d="M78 75C78 70 82 66 88 66C94 66 98 70 98 76C98 82 92 84 88 88" stroke="var(--color-text-tertiary)" strokeWidth="4" strokeLinecap="round"/>
      <circle cx="88" cy="98" r="3" fill="var(--color-text-tertiary)"/>
      
      {/* Decorative elements */}
      <circle cx="45" cy="60" r="4" fill="var(--color-brand-primary)" fillOpacity="0.3"/>
      <circle cx="135" cy="55" r="6" fill="var(--color-brand-primary)" fillOpacity="0.2"/>
      <circle cx="155" cy="90" r="3" fill="var(--color-brand-primary)" fillOpacity="0.4"/>
      
      {/* Small lines */}
      <path d="M40 100L50 100" stroke="var(--color-border-default)" strokeWidth="2" strokeLinecap="round"/>
      <path d="M45 110L55 110" stroke="var(--color-border-light)" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  )
}

export function ClosedIllustration({ className = 'w-32 h-32' }: IllustrationProps) {
  return (
    <svg className={className} viewBox="0 0 200 200" fill="none">
      {/* Moon */}
      <circle cx="140" cy="50" r="25" fill="var(--color-surface-tertiary)" stroke="var(--color-border-default)" strokeWidth="2"/>
      <circle cx="150" cy="45" r="20" fill="var(--color-surface-secondary)"/>
      
      {/* Stars */}
      <path d="M60 40L62 46L68 46L63 50L65 56L60 52L55 56L57 50L52 46L58 46Z" fill="var(--color-brand-primary)" fillOpacity="0.5"/>
      <path d="M90 60L91 63L94 63L91.5 65L92.5 68L90 66L87.5 68L88.5 65L86 63L89 63Z" fill="var(--color-brand-primary)" fillOpacity="0.3"/>
      <path d="M170 80L171 82L173 82L171.5 83.5L172 85.5L170 84L168 85.5L168.5 83.5L167 82L169 82Z" fill="var(--color-brand-primary)" fillOpacity="0.4"/>
      
      {/* Clock */}
      <circle cx="100" cy="120" r="45" fill="var(--color-surface-elevated)" stroke="var(--color-border-default)" strokeWidth="3"/>
      <circle cx="100" cy="120" r="38" fill="none" stroke="var(--color-border-light)" strokeWidth="2"/>
      
      {/* Clock hands */}
      <path d="M100 120V85" stroke="var(--color-text-primary)" strokeWidth="3" strokeLinecap="round"/>
      <path d="M100 120L125 120" stroke="var(--color-brand-primary)" strokeWidth="3" strokeLinecap="round"/>
      <circle cx="100" cy="120" r="5" fill="var(--color-brand-primary)"/>
      
      {/* Hour markers */}
      <circle cx="100" cy="82" r="2" fill="var(--color-text-tertiary)"/>
      <circle cx="138" cy="120" r="2" fill="var(--color-text-tertiary)"/>
      <circle cx="100" cy="158" r="2" fill="var(--color-text-tertiary)"/>
      <circle cx="62" cy="120" r="2" fill="var(--color-text-tertiary)"/>
      
      {/* ZZZ */}
      <text x="155" y="100" fill="var(--color-text-tertiary)" fontSize="14" fontWeight="bold">Z</text>
      <text x="165" y="90" fill="var(--color-text-tertiary)" fontSize="12" fontWeight="bold">z</text>
      <text x="172" y="82" fill="var(--color-text-tertiary)" fontSize="10" fontWeight="bold">z</text>
    </svg>
  )
}
